import React, { useRef } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Drawer, GroupsFilters } from '@pkg/components';
import Dropdown from '@pkg/components/Dropdown';
import { Button } from '@pkg/components/Sections/Profile/Buttons';

import { type GroupsFilter } from '@app/client/common/structs/groups/groups';

import { ActionsGroup, Description, DropdownFilterHeading } from './styles';

interface Props {
    isOpen: boolean;
    selectedFilter?: GroupsFilter;
    filterOptions?: GroupsFilter[];
    setIsOpen(val: boolean): void;
    onClickFilter?(selected: GroupsFilter): void;
    clearFilter?(): void;
}

export const GroupsFiltersModal = ({
    isOpen,
    selectedFilter,
    filterOptions,
    setIsOpen,
    onClickFilter,
    clearFilter,
}: Props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const prevFiltersRef = useRef('');

    const { t: translate } = useTranslation(['yat_nft_manager', 'yat_page', 'groups']);

    const handleClose = () => {
        setIsOpen(false);
        // Setting it as filters would already be applied when closing
        prevFiltersRef.current = location.search;
    };

    const onClearFilters = () => {
        clearFilter?.();
        navigate(location.pathname);
    };

    const handleSubmit = () => {
        setIsOpen(false);
        prevFiltersRef.current = location.search;
    };

    const disableClear = !location.search;
    const disableApplyFilters = prevFiltersRef.current === location.search || disableClear;
    // TODO: <AnimatePresence /> should live OUTSIDE this component,
    // and the conditional rendering is handled by the parent.
    // Otherwise, you are mounting this component and executing
    // side-effects even when `isOpen === false`.

    const showDropdown = selectedFilter && filterOptions && onClickFilter;
    const drawerMarkup = isOpen ? (
        <Drawer onClose={handleClose}>
            <Drawer.Header onClose={handleClose} />
            <Drawer.Main>
                <Drawer.Main.Section>
                    <Description>{translate('groups:groups_modal_description')}</Description>
                    {showDropdown && (
                        <>
                            <DropdownFilterHeading>
                                {translate('groups:groups_filter_dropdown_title')}
                            </DropdownFilterHeading>
                            <Dropdown selected={selectedFilter} options={filterOptions} onClick={onClickFilter} />
                        </>
                    )}
                    <GroupsFilters />
                </Drawer.Main.Section>
            </Drawer.Main>

            <Drawer.Footer sticky>
                <ActionsGroup>
                    <Button
                        onClick={onClearFilters}
                        variant="solid"
                        color="secondary"
                        size="medium"
                        fluid
                        disabled={disableClear}
                    >
                        {translate('yat_nft_manager:yat_nft_manager_filter_clear_filters')}
                    </Button>

                    <Button onClick={handleSubmit} size="medium" fluid disabled={disableApplyFilters}>
                        {translate('yat_nft_manager:yat_nft_manager_filter_apply')}
                    </Button>
                </ActionsGroup>
            </Drawer.Footer>
        </Drawer>
    ) : null;

    return <AnimatePresence mode="wait">{drawerMarkup}</AnimatePresence>;
};
